import React from 'react'
import { Field, Form, Formik } from 'formik'
import { Columns, Heading, Element, Box, Message } from 'react-bulma-components'
import InputField from '../../form/fields/input'
import SelectField from '../../form/fields/select'
import CheckboxField from '../../form/fields/checkbox'
import CheckboxesField from '../../form/fields/checkboxes'
import MultiSelectField from '../../form/fields/multi-select'
import InputFormulaField from '../../form/fields/input-formula-field'
import {
  useCreateMutation,
  useUpdateMutation,
  useEmployerRulesQuery,
} from '../../../queries/employer-rules'
import { useSpecialPeriodTypesQuery } from '../../../queries/special-periods'
import { useWorkPeriodEventsQuery } from '../../../queries/work-period-events'
import { RequestButton } from '../../request-components/request-components'
import { useNavigate, useLocation } from 'react-router-dom'
import { oneHour } from '../../../utils/date'
import { buildListAsInputOptions } from '../../../utils/forms'
import { EmployerRulesFormProps, FormValues } from './types'
import { validateEmployerRuleForm } from './validation'
import { transformInitialValues, transformFormToPayload } from './utils'
import ValuesInTimeSection from './values-in-time-section'
import BoundsConfigSection from './bounds-config-section'
import { getAvailablePathsForContext } from './input-formula-utils'

/**
 * Main EmployerRulesForm component
 * Handles form state, validation, and submission for employer rules
 */
const EmployerRulesForm: React.FC<EmployerRulesFormProps> = ({ employerRule, employerId }) => {
  const createMutation = useCreateMutation()
  const updateMutation = useUpdateMutation()
  const navigate = useNavigate()
  const location = useLocation()

  // Determine base path from current location
  const basePath = location.pathname.includes('/employers/')
    ? `/employers/${employerId}`
    : '/settings'

  React.useEffect(() => {
    if (createMutation.isSuccess) navigate(`${basePath}/employer-rules/${createMutation.data._id}`)
  }, [createMutation.isSuccess, basePath, navigate])

  const initialValues = transformInitialValues(employerRule, employerId)

  return (
    <Formik<FormValues>
      initialValues={initialValues}
      enableReinitialize
      validate={validateEmployerRuleForm}
      onSubmit={values => {
        const payload = transformFormToPayload(values)

        if (!employerRule) {
          createMutation.mutate(payload as any)
        } else {
          updateMutation.mutate({
            id: employerRule._id,
            ...payload,
          } as any)
        }
      }}
    >
      {({ values }) => {
        // Days of the week configuration (Lundi = 0, Dimanche = 6)
        const daysOfWeekOptions = [
          { value: 0, label: 'Lundi' },
          { value: 1, label: 'Mardi' },
          { value: 2, label: 'Mercredi' },
          { value: 3, label: 'Jeudi' },
          { value: 4, label: 'Vendredi' },
          { value: 5, label: 'Samedi' },
          { value: 6, label: 'Dimanche' },
        ]

        // Fetch special period types for the employer
        const specialPeriodTypesQuery = useSpecialPeriodTypesQuery(employerId)

        // Fetch all employer rules for the employer (for formula references)
        const employerRulesQuery = useEmployerRulesQuery({ employer: employerId })

        // Prepare options for special period types
        const specialPeriodTypesOptions = React.useMemo(() => {
          if (!specialPeriodTypesQuery.data) return []
          return buildListAsInputOptions(specialPeriodTypesQuery.data)
        }, [specialPeriodTypesQuery.data])

        // Prepare options for employer rules (for formula references)
        // Filter out the current rule to prevent self-reference
        const availableRulesOptions = React.useMemo(() => {
          if (!employerRulesQuery.data) return []

          // Only allow referencing rules with the same period as the current rule.
          // Notes:
          // - when building a 'day' formula, only show 'day' rules
          // - when building a 'week' formula, we allow both 'week' and 'day' rules
          const currentPeriod = values.interval?.period

          return employerRulesQuery.data
            .filter(rule => !employerRule || rule._id !== employerRule._id)
            .filter(rule => {
              if (!currentPeriod) return true

              const rulePeriod = rule.interval?.period

              if (currentPeriod === 'week') {
                return rulePeriod === 'week' || rulePeriod === 'day'
              }

              return rulePeriod === currentPeriod
            })
            .map(rule => ({
              value: rule._id,
              label: `${rule.name} (${rule.shortName || rule.name})`,
            }))
        }, [employerRulesQuery.data, employerRule, values.interval?.period])

        // Fetch work period events for the employer (for eventPath formula)
        const workPeriodEventsQuery = useWorkPeriodEventsQuery({ employer: employerId })

        const availableEventsOptions = React.useMemo(() => {
          if (!workPeriodEventsQuery.data) return []
          return workPeriodEventsQuery.data.map(event => ({
            value: event._id,
            label: `${event.code} - ${event.title} (${event.type === 'benefit' ? 'Prestation' : 'Informatif'})`,
          }))
        }, [workPeriodEventsQuery.data])

        // Determine if any filter is applied
        const hasSpecialPeriodFilter = (values.specialPeriods?.types?.length || 0) > 0

        return (
          <Form autoComplete="off">
            <Heading size={5} mt={5}>
              Informations de base
            </Heading>
            {/* Basic Information */}
            <Columns>
              <Columns.Column size={6}>
                <Field
                  label="Nom "
                  name="name"
                  component={InputField}
                  required
                  disabled={Boolean(employerRule)}
                  help={
                    <Element renderAs="strong">Ne peut pas être modifié après la création</Element>
                  }
                />
              </Columns.Column>
              <Columns.Column size={6}>
                <Field
                  label="Nom court"
                  name="shortName"
                  component={InputField}
                  required
                  help={'Utilisé pour les entêtes des colonnes dans les récapitulatifs'}
                />
              </Columns.Column>
            </Columns>

            <Heading size={5} mt={5}>
              Données pour le calcul
            </Heading>

            <Columns>
              {/* Period Configuration */}
              <Columns.Column size={12}>
                <Field
                  label="Période complète ou jour séparé"
                  name="interval.period"
                  fullwidth
                  component={SelectField}
                  options={[
                    { value: 'day', label: 'Calcul par jour' },
                    { value: 'week', label: 'Calcul par semaine / période' },
                  ]}
                  required
                  info={
                    <>
                      <Element renderAs="p">
                        <Element renderAs="strong">Calcul par jour :</Element> La prime est calculée
                        séparément pour chaque jour qui correspond aux critères de la règle.
                      </Element>
                      <Element renderAs="p" mb={4}>
                        Exemple : Si une prime de 2€ par heure est appliquée le lundi et le mardi,
                        chaque jour est calculé indépendamment. 8h le lundi = 16€, 8h le mardi =
                        16€, total = 32€.
                      </Element>
                      <Element renderAs="p">
                        <Element renderAs="strong">Calcul par semaine / période :</Element> La prime
                        est calculée sur l'ensemble de la période en fonction des critères définis.
                      </Element>
                      <Element renderAs="p">
                        Exemple : Si une prime de 50€ est appliquée quand l'objectif de 40 heures
                        est atteint sur la semaine, la prime est calculée une seule fois pour toute
                        la semaine si l'objectif est atteint, et non pas jour par jour.
                      </Element>
                    </>
                  }
                />
              </Columns.Column>

              {/* Days of Week Selection (for day and week periods) */}
              {(values.interval.period === 'day' || values.interval.period === 'week') && (
                <Columns.Column size={12}>
                  <Field
                    label="Jours de la semaine"
                    name="interval.daysOfWeek"
                    component={CheckboxesField}
                    options={daysOfWeekOptions}
                    required
                    columnSize="narrow"
                    info={
                      <>
                        <Element renderAs="p">
                          Sélectionnez les jours de la semaine où cette règle de prime doit
                          s'appliquer.
                        </Element>
                        <Element renderAs="p" mb={4}>
                          Exemple : Si vous sélectionnez uniquement "Lundi" et "Mardi", la prime
                          sera calculée uniquement pour ces deux jours de la semaine, et non pour
                          les autres jours.
                        </Element>
                        <Element renderAs="p">
                          <Element renderAs="strong">Calcul par jour :</Element> La prime est
                          appliquée séparément pour chaque jour sélectionné.
                        </Element>
                        <Element renderAs="p">
                          <Element renderAs="strong">Calcul par semaine :</Element> La prime est
                          appliquée sur l'ensemble de la semaine si les jours sélectionnés sont
                          concernés.
                        </Element>
                      </>
                    }
                  />
                </Columns.Column>
              )}

              {/* Use Calendar Days (only for day period) */}
              <Columns.Column size={12}>
                <Field
                  label="Utiliser les jours calendaires"
                  name="interval.useCalendarDays"
                  component={CheckboxField}
                  info={
                    <>
                      <Element renderAs="p">
                        <Element renderAs="strong">Si coché :</Element> Prend uniquement en compte
                        la durée concernée entre 00h00 et 23h59 du jour de la règle.
                      </Element>
                      <Element renderAs="p" mb={4}>
                        Exemple : Si un travailleur commence à 22h le lundi et finit à 6h le mardi,
                        seules les heures entre 0h et 6h du mardi seront prises en compte pour le
                        calcul de la prime du mardi.
                      </Element>
                      <Element renderAs="p">
                        <Element renderAs="strong">Si non coché :</Element> Prend en compte les
                        heures comptabilisées sur la journée de travail.
                      </Element>
                      <Element renderAs="p">
                        Exemple : Si un travailleur commence à 22h le lundi et finit à 6h le mardi,
                        toutes les heures travaillées sur la journée de travail seront prises en
                        compte pour le calcul de la prime du lundi.
                      </Element>
                    </>
                  }
                />
              </Columns.Column>
              {/* Calculation Mode */}
              <Columns.Column size={12}>
                <Field
                  label="Mode de calcul de la prime"
                  name="kind"
                  component={SelectField}
                  options={[
                    { value: 'add', label: "Ajout d'une valeur fixe pour chaque heure" },
                    {
                      value: 'multiply',
                      label: 'Multiplication du coût horaire (pourcentage) pour chaque heure',
                    },
                    {
                      value: 'goal',
                      label: "Prime - valeur ajoutée quand l'objectif est atteint",
                    },
                  ]}
                  required
                  fullwidth
                  info={
                    <>
                      <Element renderAs="p">
                        <Element renderAs="strong">Ajout d'une valeur fixe :</Element> Une valeur
                        fixe est ajoutée pour chaque heure travaillée.
                      </Element>
                      <Element renderAs="p" mb={4}>
                        Exemple : Si la valeur est 2€, 8 heures travaillées = 8 × 2€ = 16€ de prime.
                      </Element>
                      <Element renderAs="p">
                        <Element renderAs="strong">Multiplication du coût horaire :</Element> Le
                        coût horaire est multiplié par un pourcentage pour chaque heure.
                      </Element>
                      <Element renderAs="p" mb={4}>
                        Exemple : Si le coût horaire est 10€ et le multiplicateur 1.5 (150%), chaque
                        heure coûte 15€ (10€ + 5€ de prime). Pour 8 heures : 8 × 15€ = 120€ (40€ de
                        prime).
                      </Element>
                      <Element renderAs="p">
                        <Element renderAs="strong">Prime quand l'objectif est atteint :</Element>{' '}
                        Une prime fixe est ajoutée uniquement si l'objectif défini est atteint.
                      </Element>
                      <Element renderAs="p">
                        Exemple : Si l'objectif est de 40 heures et la prime de 50€, la prime de 50€
                        est ajoutée si les 40 heures sont atteintes.
                      </Element>
                    </>
                  }
                />
              </Columns.Column>

              {/* Values in Time Section */}
              <Columns.Column size={12}>
                <Box backgroundColor="light">
                  <Heading size={5} mt={1}>
                    Valeurs appliquées
                    <p className="help mb-4">
                      Les périodes doivent être continues et sans chevauchement. Si une période est
                      sans date de fin, elle doit être la plus récente.
                    </p>
                  </Heading>
                  <ValuesInTimeSection values={values.valuesInTime} />
                </Box>
              </Columns.Column>

              {/* Interval Mode (only for day period) */}
              {values.interval.period === 'day' && (
                <Columns.Column size={12}>
                  <Field
                    label="Mode de calcul de l'intervalle"
                    name="interval.mode"
                    component={SelectField}
                    options={[
                      { value: 'duration', label: 'Durée' },
                      { value: 'timeRange', label: 'Plage horaire' },
                    ]}
                    required
                    fullwidth
                    info={
                      <>
                        <Element renderAs="p">
                          <Element renderAs="strong">Durée :</Element> Calcule directement sur la
                          durée totale travaillée, sans tenir compte des plages horaires spécifiques
                          de chaque période.
                        </Element>
                        <Element renderAs="p" mb={4}>
                          Exemple : Si l'intervalle est défini entre 8h et 18h et qu'un travailleur
                          a travaillé de 7h à 10h puis de 12h à 20h, la durée totale travaillée (3h
                          + 8h = 11h) sera utilisée pour le calcul, indépendamment de l'intervalle
                          défini.
                        </Element>
                        <Element renderAs="p">
                          <Element renderAs="strong">Plage horaire :</Element> Ne prend en compte
                          que les heures travaillées qui se trouvent dans l'intervalle défini (entre
                          min et max).
                        </Element>
                        <Element renderAs="p">
                          Exemple : Si l'intervalle est défini entre 8h et 18h et qu'un travailleur
                          a travaillé de 7h à 10h puis de 12h à 20h, seules les heures entre 8h et
                          18h seront prises en compte : 2h (de 8h à 10h) + 6h (de 12h à 18h) = 8h
                          pour le calcul de la prime.
                        </Element>
                      </>
                    }
                  />
                </Columns.Column>
              )}

              {/* Input Formula */}
              {values.interval?.mode === 'duration' && (
                <Columns.Column size={12}>
                  <Field
                    label="Formule d'input - Valeur prise en compte"
                    name="interval.inputFormula"
                    component={InputFormulaField}
                    availablePaths={getAvailablePathsForContext(
                      values.interval.period || '',
                      values.interval.mode,
                    )}
                    availableRules={availableRulesOptions}
                    availableEvents={availableEventsOptions}
                    disabled={!values.interval?.period}
                    required
                    info={
                      <>
                        <Element renderAs="p" mb={2}>
                          <Element renderAs="strong">Formule d'input :</Element> Définit quelle
                          valeur doit être utilisée comme base pour le calcul de cette règle.
                        </Element>
                        <Element renderAs="p" mb={2}>
                          <Element renderAs="strong">Mode simple :</Element> Sélectionnez un path
                          unique parmi les options disponibles (ex: durations.worked.total pour les
                          heures totales travaillées).
                        </Element>
                        <Element renderAs="p" mb={2}>
                          <Element renderAs="strong">Mode avancé :</Element> Composez des formules
                          complexes en combinant plusieurs paths ou références à d'autres règles
                          avec des opérations (addition, soustraction).
                        </Element>
                      </>
                    }
                  />
                </Columns.Column>
              )}
            </Columns>

            {/* Bounds Configuration */}
            <Heading size={5} mt={5}>
              Bornes de l'intervalle
            </Heading>
            <Box>
              <BoundsConfigSection
                boundsType="min"
                values={values.interval}
                availableEvents={availableEventsOptions}
              />
            </Box>
            <Box>
              <BoundsConfigSection
                boundsType="max"
                values={values.interval}
                availableEvents={availableEventsOptions}
              />
            </Box>

            {/* Special Periods Configuration */}
            <Heading size={5} mt={5}>
              Application pendant les périodes spéciales
            </Heading>
            {/* UI-only checkbox to enable/disable special periods filters */}
            <Field
              text="Appliquer la règle à des périodes spéciales"
              name="useSpecialPeriodsFilters"
              component={CheckboxField}
            />
            {values.useSpecialPeriodsFilters && (
              <Columns>
                {/* Conditional Message when filters are enabled */}
                <Columns.Column size={12}>
                  <Message color="info" mb={0}>
                    <Message.Body>
                      {!hasSpecialPeriodFilter ? (
                        <Element renderAs="strong">
                          Aucune période spéciale sélectionnée : la règle s'applique aux journées
                          normales
                        </Element>
                      ) : (
                        <Element renderAs="strong">
                          La règle s'applique uniquement pendant les périodes spéciales
                          correspondantes
                        </Element>
                      )}
                    </Message.Body>
                  </Message>
                </Columns.Column>
              </Columns>
            )}
            {/* When filters are enabled, show configuration section */}
            {values.useSpecialPeriodsFilters && (
              <Columns>
                {/* Special Period Types Selection */}
                <Columns.Column size={12}>
                  <Field
                    label="Types de périodes spéciales"
                    name="specialPeriods.types"
                    component={MultiSelectField}
                    options={specialPeriodTypesOptions}
                    info={
                      <>
                        <Element renderAs="p">
                          Sélectionnez un ou plusieurs types de périodes spéciales. La règle
                          s'appliquera si une période spéciale correspond à l'un des types
                          sélectionnés.
                        </Element>
                        <Element renderAs="p">
                          Exemple : Si vous sélectionnez "Jour férié", la règle s'appliquera pour
                          tous les jours fériés de l'employeur.
                        </Element>
                      </>
                    }
                  />
                </Columns.Column>
              </Columns>
            )}
            <Columns>
              {/* Multiply Mode Options */}
              {values.kind === 'multiply' && (
                <Columns.Column size={12} mt={4}>
                  <Field
                    label="Enregistrer la durée comme différence de la multiplication"
                    name="employerDataOptions.setDurationAsProportional"
                    component={CheckboxField}
                    help={
                      <div>
                        Exemple
                        <br />
                        Non: La durée enregistrée est égale à la durée travaillée dans la période de
                        la règle.
                        <br />
                        Oui: Une heure * {values.valuesInTime[0]?.value || '1.5'} ={' '}
                        {(
                          (oneHour * (Number(values.valuesInTime[0]?.value) || 1.5) - oneHour) /
                          1000 /
                          60
                        ).toFixed(2)}{' '}
                        minutes
                      </div>
                    }
                  />
                </Columns.Column>
              )}

              {/* Add To Worked Total Option */}
              <Columns.Column size={12} mt={4}>
                <Field
                  text="Ajouter la durée calculée à la durée totale travaillée"
                  name="employerDataOptions.addToWorkedTotal"
                  component={CheckboxField}
                  info={
                    <>
                      <Element renderAs="p" mb={2}>
                        <Element renderAs="strong">Désactivée : </Element>
                        La durée calculée n'est pas ajoutée au total des heures travaillées. Elle
                        sert uniquement à déterminer la durée prise en pour l'application de la
                        règle.
                      </Element>
                      <Element renderAs="p">
                        <Element renderAs="strong">Activée : </Element>
                        La durée calculée par cette règle sera ajoutée au total des heures
                        comptabilisées dans la semaine.
                      </Element>
                    </>
                  }
                />
              </Columns.Column>
            </Columns>

            {/* Display Settings - Summary */}
            <Heading size={5} mt={5}>
              Paramètres d'affichage
            </Heading>
            <Field
              text="Afficher dans les totaux du récapitulatif"
              name="display.summary.totals"
              component={CheckboxField}
            />
            <Field
              text="Afficher dans les coûts du récapitulatif"
              name="display.summary.costs"
              component={CheckboxField}
            />
            {values.interval.period === 'day' && (
              <Field
                text="Afficher dans les journées du récapitulatif"
                name="display.summary.days"
                component={CheckboxField}
              />
            )}
            <Field
              text="Visible dans la vue Paie"
              name="display.payroll"
              component={CheckboxField}
            />

            {/* Submit Button */}
            <RequestButton
              color="primary"
              type="submit"
              mutation={!employerRule ? createMutation : updateMutation}
              mt={6}
            >
              {employerRule ? 'Mettre à jour' : 'Créer'}
            </RequestButton>
          </Form>
        )
      }}
    </Formik>
  )
}

export default EmployerRulesForm
